/**
 * Pure projection of utterance-stream transcript deltas (the local engine's
 * only emission shape).
 *
 * Contract recap: a delta carrying `utteranceId` describes a whole utterance.
 * Its `text` is a FULL REPLACEMENT each time; `isFinal: true` freezes the
 * utterance permanently; per-lane deltas arrive in order.
 *
 * This module owns every projection state transition. Callers (MobX stores)
 * hold the state in shallow observables and swap whole objects; nothing here
 * mutates its inputs and nothing here imports MobX.
 */
import {
  formatTranscriptDelta,
  type TranscriptInterimChunk,
} from './meetings-transcript.utils';

import type { TranscriptDeltaPayload } from './meetings.types';

export type UtteranceTranscriptDelta = TranscriptDeltaPayload & {
  utteranceId: string;
};

export type ProjectedUtterance = {
  utteranceId: string;
  speaker: string;
  text: string;
  /** Cross-lane open-order counter allocated by the producer. */
  chunkId: number;
  /** Arrival index of this utterance's run prefix (0 for the first run). */
  runIndex: number;
};

export type FinalizedUtterance = {
  utteranceId: string;
  speaker: string;
  text: string;
  chunkId: number;
  runIndex: number;
};

export type UtteranceProjectionState = {
  /** Live (non-final) utterances, in insertion order. */
  utterances: ProjectedUtterance[];
  /** Finalized utterances not yet folded into the committed transcript, kept
   * sorted by (runIndex, chunkId). One waits here while an earlier-opened
   * utterance is still live. */
  uncommittedFinals: FinalizedUtterance[];
  /** Ids that already finalized; any later delta for them is a no-op. */
  finalizedUtteranceIds: Set<string>;
  /**
   * Run prefixes in arrival order. A transcriber restart starts a new prefix
   * and may restart the chunk counter, so ordering is (runIndex, chunkId).
   */
  runIndexByPrefix: ReadonlyMap<string, number>;
};

export type NewlyFinalizedUtterance = {
  utteranceId: string;
  speaker: string;
  text: string;
  /** Order key pieces for the backend sync release gate. */
  chunkId: number;
  runPrefix: string;
};

export type ApplyUtteranceDeltaResult = {
  state: UtteranceProjectionState;
  /** Set exactly once per utteranceId, on its first 'final' delta. */
  newlyFinalized?: NewlyFinalizedUtterance;
};

export type UtteranceProjectionTraceEvent = {
  type: 'ui_utterance_projection_delta';
  source: string;
  delta: UtteranceTranscriptDelta;
  ignoredAsAlreadyFinal: boolean;
  newlyFinalized?: NewlyFinalizedUtterance;
  utterancesAfter: ProjectedUtterance[];
};

export type UtteranceProjectionTraceSink = (
  event: UtteranceProjectionTraceEvent
) => void;

type ApplyUtteranceDeltaOptions = {
  trace?: UtteranceProjectionTraceSink;
  traceSource?: string;
};

export function createUtteranceProjectionState(): UtteranceProjectionState {
  return {
    utterances: [],
    uncommittedFinals: [],
    finalizedUtteranceIds: new Set(),
    runIndexByPrefix: new Map(),
  };
}

/**
 * Routing guard: deltas carrying an utteranceId belong to the utterance
 * projection path and must never enter appendLiveTranscriptDelta.
 */
export function isUtteranceDelta(
  delta: TranscriptDeltaPayload
): delta is UtteranceTranscriptDelta {
  return delta.utteranceId !== undefined;
}

/**
 * The producer composes `utteranceId` as `"<runPrefix>-<chunkId>"`. This is
 * the only place that parses the format; everything else treats the id as
 * opaque.
 */
export function utteranceRunPrefix(utteranceId: string): string {
  const separatorIndex = utteranceId.lastIndexOf('-');
  return separatorIndex === -1
    ? utteranceId
    : utteranceId.slice(0, separatorIndex);
}

export function applyUtteranceDelta(
  state: UtteranceProjectionState,
  delta: UtteranceTranscriptDelta,
  options: ApplyUtteranceDeltaOptions = {}
): ApplyUtteranceDeltaResult {
  if (state.finalizedUtteranceIds.has(delta.utteranceId)) {
    // Defensive idempotency: the producer promises nothing arrives after
    // 'final', but a duplicate must not double-commit. Same state reference
    // back signals "no-op" to callers and tests.
    traceUtteranceDelta(options, {
      delta,
      ignoredAsAlreadyFinal: true,
      utterancesAfter: state.utterances,
    });
    return { state };
  }

  const runPrefix = utteranceRunPrefix(delta.utteranceId);
  const knownRunIndex = state.runIndexByPrefix.get(runPrefix);
  const runIndex = knownRunIndex ?? state.runIndexByPrefix.size;
  const runIndexByPrefix =
    knownRunIndex === undefined
      ? new Map(state.runIndexByPrefix).set(runPrefix, runIndex)
      : state.runIndexByPrefix;
  const liveState =
    knownRunIndex === undefined
      ? sealDeadRunUtterances(state, runIndex)
      : state;
  const existingIndex = liveState.utterances.findIndex(
    (utterance) => utterance.utteranceId === delta.utteranceId
  );

  if (delta.isFinal) {
    const finalizedUtteranceIds = new Set(liveState.finalizedUtteranceIds);
    finalizedUtteranceIds.add(delta.utteranceId);
    const finalizedUtterance: FinalizedUtterance = {
      utteranceId: delta.utteranceId,
      speaker: delta.speaker,
      text: delta.text,
      chunkId: delta.chunkId,
      runIndex,
    };
    const insertAt = liveState.uncommittedFinals.findIndex(
      (existing) => compareOpenOrder(finalizedUtterance, existing) < 0
    );
    const nextState: UtteranceProjectionState = {
      // Drop from the live list so its text is never rendered twice
      utterances: liveState.utterances.filter(
        (utterance) => utterance.utteranceId !== delta.utteranceId
      ),
      uncommittedFinals:
        insertAt === -1
          ? [...liveState.uncommittedFinals, finalizedUtterance]
          : [
              ...liveState.uncommittedFinals.slice(0, insertAt),
              finalizedUtterance,
              ...liveState.uncommittedFinals.slice(insertAt),
            ],
      finalizedUtteranceIds,
      runIndexByPrefix,
    };
    const newlyFinalized: NewlyFinalizedUtterance = {
      utteranceId: delta.utteranceId,
      speaker: delta.speaker,
      text: delta.text,
      chunkId: delta.chunkId,
      runPrefix,
    };
    traceUtteranceDelta(options, {
      delta,
      ignoredAsAlreadyFinal: false,
      newlyFinalized,
      utterancesAfter: nextState.utterances,
    });
    return { state: nextState, newlyFinalized };
  }

  const upsertedUtterance: ProjectedUtterance = {
    utteranceId: delta.utteranceId,
    speaker: delta.speaker,
    text: delta.text,
    chunkId: delta.chunkId,
    runIndex,
  };
  const nextState: UtteranceProjectionState = {
    utterances:
      existingIndex === -1
        ? [...liveState.utterances, upsertedUtterance]
        : liveState.utterances.map((utterance, index) =>
            index === existingIndex ? upsertedUtterance : utterance
          ),
    uncommittedFinals: liveState.uncommittedFinals,
    finalizedUtteranceIds: liveState.finalizedUtteranceIds,
    runIndexByPrefix,
  };
  traceUtteranceDelta(options, {
    delta,
    ignoredAsAlreadyFinal: false,
    utterancesAfter: nextState.utterances,
  });
  return { state: nextState };
}

/**
 * Everything one transcript view owns on the utterance path: the live
 * projection and the committed transcript it feeds. Stores hold these fields
 * in observables and swap them wholesale; `applyUtteranceDeltaToView` is the
 * only transition.
 */
export type UtteranceTranscriptView = {
  projection: UtteranceProjectionState;
  transcript: string;
  currentSpeaker: string | undefined;
};

export type ApplyUtteranceDeltaToViewResult = {
  view: UtteranceTranscriptView;
  /** Set exactly once per utteranceId, on its first 'final' delta. */
  newlyFinalized?: NewlyFinalizedUtterance;
};

/**
 * Commits every final the open order now allows, so the transcript grows only
 * at its end and in the order the utterances opened. During double-talk a
 * final that closed ahead of a still-live earlier utterance stays queued and
 * keeps rendering in place, instead of being appended to the transcript above
 * text that opened before it and dragging that text down the screen.
 */
export function applyUtteranceDeltaToView(
  view: UtteranceTranscriptView,
  delta: UtteranceTranscriptDelta,
  options: ApplyUtteranceDeltaOptions = {}
): ApplyUtteranceDeltaToViewResult {
  const { state, newlyFinalized } = applyUtteranceDelta(
    view.projection,
    delta,
    options
  );

  if (!newlyFinalized) {
    return { view: { ...view, projection: state } };
  }

  return {
    view: commitFinalsInOpenOrder(view, state, earliestOpenOrder(state)),
    newlyFinalized,
  };
}

/**
 * Meeting stop: commits whatever is still queued, since no further final can
 * arrive to unblock it. Live utterances are dropped with the projection, the
 * same as before this queue existed.
 */
export function flushUncommittedFinals(
  view: UtteranceTranscriptView
): UtteranceTranscriptView {
  if (view.projection.uncommittedFinals.length === 0) return view;

  return commitFinalsInOpenOrder(view, view.projection, undefined);
}

function commitFinalsInOpenOrder(
  view: UtteranceTranscriptView,
  state: UtteranceProjectionState,
  blockedFrom: { runIndex: number; chunkId: number } | undefined
): UtteranceTranscriptView {
  let transcript = view.transcript;
  let currentSpeaker = view.currentSpeaker;
  let committedCount = 0;

  for (const finalUtterance of state.uncommittedFinals) {
    if (blockedFrom && compareOpenOrder(finalUtterance, blockedFrom) > 0) break;

    transcript += formatTranscriptDelta(transcript, currentSpeaker, {
      speaker: finalUtterance.speaker,
      text: finalUtterance.text,
    });
    currentSpeaker = finalUtterance.speaker;
    committedCount += 1;
  }

  return {
    projection:
      committedCount === 0
        ? state
        : {
            ...state,
            uncommittedFinals: state.uncommittedFinals.slice(committedCount),
          },
    transcript,
    currentSpeaker,
  };
}

function earliestOpenOrder(
  state: UtteranceProjectionState
): { runIndex: number; chunkId: number } | undefined {
  let earliest: ProjectedUtterance | undefined;
  for (const utterance of state.utterances) {
    if (!earliest || compareOpenOrder(utterance, earliest) < 0) {
      earliest = utterance;
    }
  }
  return earliest;
}

/**
 * A new run prefix means the previous transcriber run is dead, so its
 * still-open utterances will never finalize. Seal them at their open-order
 * slot instead of letting them block every later final; marking them finalized
 * also keeps hypothesis deltas from a wedged lane from reopening them. Mirrors
 * the release gate in `meetings-utterance-sync-gate.ts`.
 */
function sealDeadRunUtterances(
  state: UtteranceProjectionState,
  newRunIndex: number
): UtteranceProjectionState {
  const deadRunUtterances = state.utterances.filter(
    (utterance) => utterance.runIndex < newRunIndex
  );
  if (deadRunUtterances.length === 0) return state;

  return {
    ...state,
    utterances: state.utterances.filter(
      (utterance) => utterance.runIndex >= newRunIndex
    ),
    uncommittedFinals: [...state.uncommittedFinals, ...deadRunUtterances].sort(
      compareOpenOrder
    ),
    finalizedUtteranceIds: new Set([
      ...state.finalizedUtteranceIds,
      ...deadRunUtterances.map((utterance) => utterance.utteranceId),
    ]),
  };
}

/**
 * Cross-lane "when transcription started producing it" order: run arrival
 * first (restarts may reset the chunk counter), then the producer's shared
 * open-order chunk counter.
 */
function compareOpenOrder(
  a: { runIndex: number; chunkId: number },
  b: { runIndex: number; chunkId: number }
): number {
  return a.runIndex - b.runIndex || a.chunkId - b.chunkId;
}

/**
 * Everything the transcript shows beyond the committed string: queued finals
 * (rendered at normal color) and live utterances (dimmed), merged in open
 * order. Feed these to `buildTranscriptLines` after the legacy interim
 * chunks; it applies the same `[Speaker]: text` formatting and consecutive
 * same-speaker merging the legacy path uses.
 */
export function projectUtteranceChunks(
  state: UtteranceProjectionState
): TranscriptInterimChunk[] {
  const chunks: (FinalizedUtterance & { isFinal?: true })[] = [
    ...state.uncommittedFinals.map((finalUtterance) => ({
      ...finalUtterance,
      isFinal: true as const,
    })),
    ...state.utterances,
  ];

  return chunks.sort(compareOpenOrder).map((utterance) => ({
    speaker: utterance.speaker,
    text: utterance.text,
    isFinal: utterance.isFinal,
  }));
}

function traceUtteranceDelta(
  options: ApplyUtteranceDeltaOptions,
  event: Omit<UtteranceProjectionTraceEvent, 'type' | 'source'>
): void {
  if (!options.trace) return;

  options.trace({
    type: 'ui_utterance_projection_delta',
    source: options.traceSource ?? 'unknown',
    ...event,
  });
}
